import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Item {
  id: string;
  label: string;
  link: string;
  icon?: string;
  roles?: string[];
  onClick?: (id: string) => void;
}

export interface NavItem {
  label: string;
  items: Item[];
}

export interface MetaNavItem {
  label: string;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menu$: BehaviorSubject<NavItem[]> = new BehaviorSubject<NavItem[]>([]);
  private filteredMenu$: BehaviorSubject<NavItem[]> = new BehaviorSubject<NavItem[]>([]);
  private metaMenu$: BehaviorSubject<MetaNavItem[]> = new BehaviorSubject<MetaNavItem[]>([]);

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.menu$.subscribe(items => {
      this.authService.getRoles().subscribe(roles => {
        const filtered = items
          .map(item => {
            return {
              label: item.label,
              items: item.items.filter(this.roleFilter(roles)),
            } as NavItem;
          })
          .filter(x => x.items.length > 0);
        this.filteredMenu$.next(filtered);
      });
    });
  }

  getFilteredMenu(): Observable<NavItem[]> {
    return this.filteredMenu$.asObservable();
  }

  getMetaNav(): Observable<MetaNavItem[]> {
    return this.metaMenu$.asObservable();
  }

  setMenu(nav: NavItem[]) {
    nav.forEach(item => {
      item.label = item.label !== '' ? this.translateService.instant(item.label) : '';
      item.items.forEach(subItem => {
        subItem.label = subItem.label !== '' ? this.translateService.instant(subItem.label) : '';
        subItem.onClick = () => {
          this.router.navigateByUrl(subItem.link);
        };
      });
    });

    this.menu$.next(nav);
  }

  setMetaMenu(metaNav: MetaNavItem[]) {
    metaNav.forEach(item => {
      item.label = this.translateService.instant(item.label);
    });

    this.metaMenu$.next(metaNav);
  }

  private roleFilter(userRoles: string[] | undefined) {
    return item => {
      if (item.roles && item.roles.length > 0) {
        let hasRole = false;
        for (const roleName of item.roles) {
          hasRole = !!userRoles?.includes(roleName);
          if (hasRole) {
            break;
          }
        }
        return hasRole ? item : false;
      }

      return item;
    };
  }
}
